/**
 * **The *Password* column and *Save password*** (W22, FR-58, plan D56; W30,
 * FR-67, ADR-0021, D103) — on the library's row and on the document's page,
 * beside its type.
 *
 * What it says comes from the service, never a guess: *Not required*,
 * *Locked*, *Wrong password — try again*, *Unlocked* ✓ (read with a password
 * typed for one parse) and, from W30, **Saved** — the current version's
 * password is kept, sealed, and used for the reader: it opens for them, nobody
 * is shown it, and the file stays protected (`password_saved`, read through
 * `passwordSavedOf`).
 *
 * **The box saves the password** (W30): a `PUT` of the current version's
 * password, which the service checks against the file before it keeps it
 * (`document:can_update`). Right → *Saved*; wrong → *Wrong password — try
 * again*, and nothing is kept. **Saved once, then final** (the owner,
 * 2026-10-24): no change and no forget, so no box is offered again for that
 * version. The box is empty again as soon as it is sent; the password is in
 * that one request's body and nowhere else — never shown back, never in browser
 * storage, never logged. Saving asks no parse (ADR-0013): a reading is still
 * asked for, from *Read it as…*.
 */
import { Button, Input, TONE_TEXT, Tooltip, cn } from '@fiveox/ui'
import { CircleCheck, KeyRound, LoaderCircle, LockKeyhole } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { PASSWORD_WORDS, SAVED_NOTE, UNLOCKED_NOTE, passwordErrorText } from './extraction-model'
import { errorText } from './hooks'
import { ApiError, passwordSavedOf, type ProDocClient, type ProDocDocument } from './prodoc-client'

/** How a save ended — the host re-reads the document so the column is the service's again. */
export type PasswordSaveOutcome = 'saved' | 'wrong' | 'failed'

/** W22's name for the outcome, kept for a host that typed it. */
export type UnlockOutcome = PasswordSaveOutcome

type Trying = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'wrong' } | { kind: 'failed'; message: string }

export interface PasswordStatusProps {
  client: ProDocClient
  doc: Pick<ProDocDocument, 'id' | 'current_version_id' | 'media_type' | 'password'> & Partial<Pick<ProDocDocument, 'encrypted' | 'password_saved' | 'current_version'>>
  /** `document:can_update`: whether this person may save the password, and so see the box. */
  mayUpdate: boolean
  /** The current version's password is saved — from its versions, where the record does not say (`passwordSavedOf` otherwise). */
  saved?: boolean
  /** The save ended — the host re-reads the document. */
  onSaved?: (outcome: PasswordSaveOutcome) => void
  /** Offer the box beside *Unlocked* too — the detail page, where a password typed for one reading can still be kept. */
  offerWhenUnlocked?: boolean
  /** `row`: a table cell — compact; clicks and keys stay in it, so the row does not open. */
  layout?: 'row' | 'inline'
  className?: string
}

/** Why a save was refused, as the person can act on it — the password itself is never in it. */
function refusalOf(e: unknown): Trying {
  const reason = e instanceof ApiError ? e.reason : undefined
  if (reason === 'password_incorrect') return { kind: 'wrong' }
  if (reason === 'not_encrypted' || reason === 'password_unsupported' || reason === 'password_already_saved') return { kind: 'failed', message: passwordErrorText(reason) }
  return { kind: 'failed', message: errorText(e, 'ProDoc did not save the password.') }
}

/** FR-58's word for the current version, with FR-67's *Saved* — and the box with *Save password* where this person may save it. Nothing for a document whose answer carried no `password`. */
export function PasswordStatus({ client, doc, mayUpdate, saved: savedProp, onSaved, offerWhenUnlocked = false, layout = 'inline', className }: PasswordStatusProps) {
  const id = useId()
  const [value, setValue] = useState('')
  const [trying, setTrying] = useState<Trying>({ kind: 'idle' })
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])
  // A new version is a new question.
  useEffect(() => setTrying({ kind: 'idle' }), [doc.current_version_id])

  const state = doc.password
  const saved = savedProp === true || passwordSavedOf(doc) || trying.kind === 'saved'
  if (!state && !saved) return null
  const set = (next: Trying) => {
    if (live.current) setTrying(next)
  }

  if (saved) {
    // Final for this version (the owner, 2026-10-24): no box, no change, no forget.
    return (
      <span className={cn('inline-flex items-center', className)} data-password="saved">
        <Tooltip label={SAVED_NOTE}>
          <span tabIndex={0} className={cn('inline-flex items-center gap-1 text-sm font-medium', TONE_TEXT.success)} title={SAVED_NOTE}>
            <KeyRound size={14} aria-hidden />
            {layout === 'row' ? PASSWORD_WORDS.saved : 'Password saved'}
          </span>
        </Tooltip>
      </span>
    )
  }
  if (state === 'not_required') return <span className={cn('text-muted-foreground text-sm', className)}>{PASSWORD_WORDS.not_required}</span>

  const unlocked = state === 'unlocked'
  const unlockedWord = (
    <Tooltip label={UNLOCKED_NOTE}>
      <span tabIndex={0} className={cn('inline-flex items-center gap-1 text-sm font-medium', TONE_TEXT.success)} title={UNLOCKED_NOTE}>
        {PASSWORD_WORDS.unlocked}
        <CircleCheck size={14} aria-hidden />
      </span>
    </Tooltip>
  )
  // Read with a password typed once: the word alone — the detail page may still offer to keep it.
  if (unlocked && !(offerWhenUnlocked && mayUpdate)) {
    return (
      <span className={cn('inline-flex items-center', className)} data-password="unlocked">
        {unlockedWord}
      </span>
    )
  }

  // Locked, or the last try was wrong: the box, where this person may save it — a PDF's only (ADR-0021).
  const offer = mayUpdate && doc.media_type === 'application/pdf'
  const wrong = trying.kind === 'wrong' || (trying.kind === 'idle' && state === 'wrong')
  const busy = trying.kind === 'saving'

  const save = async () => {
    if (!value || busy) return
    const password = value
    // The box is empty again at once; the password lives only in this request.
    setValue('')
    set({ kind: 'saving' })
    try {
      await client.documents.savePassword(doc.id, doc.current_version_id, password)
      set({ kind: 'saved' })
      onSaved?.('saved')
    } catch (e) {
      const next = refusalOf(e)
      set(next)
      onSaved?.(next.kind === 'wrong' ? 'wrong' : 'failed')
    }
  }

  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
  return (
    <div
      className={cn(layout === 'row' ? 'space-y-1' : 'inline-flex flex-wrap items-center gap-2', className)}
      data-password={wrong ? 'wrong' : unlocked ? 'unlocked' : 'locked'}
      // A table row opens on a click; typing a password must not open it.
      onClick={layout === 'row' ? stop : undefined}
      onKeyDown={layout === 'row' ? stop : undefined}
    >
      {unlocked && !wrong ? (
        unlockedWord
      ) : (
        <span className={cn('inline-flex items-center gap-1 text-sm', wrong ? TONE_TEXT.error : TONE_TEXT.warning)}>
          <LockKeyhole size={14} aria-hidden />
          {wrong ? PASSWORD_WORDS.wrong : PASSWORD_WORDS.locked}
        </span>
      )}
      {offer && (
        <span className="flex items-center gap-1.5">
          <label htmlFor={id} className="sr-only">
            Password
          </label>
          <Input
            id={id}
            size="sm"
            type="password"
            autoComplete="off"
            placeholder="Password"
            className={layout === 'row' ? 'w-28' : 'w-40'}
            value={value}
            disabled={busy}
            aria-invalid={wrong ? true : undefined}
            onChange={(e) => {
              setValue(e.target.value)
              if (trying.kind === 'wrong' || trying.kind === 'failed') setTrying({ kind: 'idle' })
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void save()
              }
            }}
          />
          <Button size="sm" variant="outline" disabled={!value || busy} onClick={() => void save()}>
            {busy ? <LoaderCircle size={14} className="animate-spin" aria-hidden /> : null}
            Save password
          </Button>
        </span>
      )}
      {busy && <span className="text-muted-foreground text-xs">Checking…</span>}
      {trying.kind === 'failed' && (
        <span className={cn('block text-xs', TONE_TEXT.error)} role="alert">
          {trying.message}
        </span>
      )}
    </div>
  )
}
